import { CalendarIcon, ExpenseIcon, GroupsIcon, IdeaIcon, SettingsIcon } from '@/assets/icons'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { convertDateRangeToPayload, useEvents, useIdeas, useMe, useTrip, useTripMembers, useUpdateTripDates } from '@hooks'
import { TripHelper } from '@lib/TripHelper'
import axios from 'axios'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import EditTripDatesModal from '../../Itinerary/EditTripDatesModal/EditTripDatesModal'
import EditTripModal from '../EditTripModal'
import {
    DashboardCard,
    DashboardCardContent,
    DashboardCardDescription,
    DashboardCardFooter,
    DashboardCardHeader,
    DashboardCardIcon,
    DashboardCardMetric,
    DashboardCardMetrics,
    DashboardCardTitle,
    DashboardContainer,
    DashboardGrid,
    DashboardHeader,
    DashboardTitle,
    DashboardTitleSection,
    DashboardWrapper,
    DateInfoContainer,
    EmptyStateMessage,
    ErrorState,
    ExpensesMessage,
    LoadingState,
    NavigateButton,
    SettingsButton,
    SettingsButtonContainer,
    TripCode,
    TripCodeContainer,
    TripCodeLabel
} from './TripDetailPage.styles'

const TripDetailPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: me } = useMe()
	const { data: eventsData } = useEvents(tripId)
	const { data: ideasData } = useIdeas(tripId)
	const { data: membersData } = useTripMembers(tripId)
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false)
	const updateTripDates = useUpdateTripDates(tripId || '')

	// Set breadcrumbs - must be called before any early returns
	useBreadcrumbs(
		trip
			? [
					{ label: 'My Trips', path: '/trips' },
					{ label: trip.name },
				]
			: []
	)

	if (tripLoading) {
		return (
			<DashboardWrapper>
				<DashboardContainer>
					<LoadingState>Loading trip details...</LoadingState>
				</DashboardContainer>
			</DashboardWrapper>
		)
	}

	if (!trip) {
		return (
			<DashboardWrapper>
				<DashboardContainer>
					<ErrorState>Trip not found</ErrorState>
				</DashboardContainer>
			</DashboardWrapper>
		)
	}

	const eventsCount = eventsData?.events?.length || 0
	const ideasCount = ideasData?.ideas?.length || 0
	const membersCount = membersData?.members?.length || 0

	const tripHelper = new TripHelper(trip)
	const hasTripDates = !!trip.startDate
	const startDate = trip.startDate ? TripHelper.formatDate(trip.startDate) : 'Not set'
	const endDate = trip.startDate && trip.dayCount > 0 ? tripHelper.getFormattedEndDate() : 'Not set'
	const daysCount = trip.dayCount || 0

	const tripStartDate = trip?.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = trip
		? (() => {
				if (!trip.startDate || !trip.dayCount) return ''
				const tripHelper = new TripHelper(trip)
				const endDate = tripHelper.getEndDate()
				return endDate ? endDate.toISOString().split('T')[0] : ''
			})()
		: ''

	const handleItineraryClick = () => {
		if (!hasTripDates) {
			setIsEditModalOpen(true)
		} else {
			navigate(`/trips/${tripId}/itinerary`)
		}
	}

	const handleSaveTripDates = (startDate: string, endDate: string) => {
		if (trip && tripId) {
			const payload = convertDateRangeToPayload({ startDate, endDate })
			updateTripDates.mutate(payload, {
				onSuccess: () => {
					toast.success('Trip dates updated successfully!')
					setIsEditModalOpen(false)
				},
				onError: (error) => {
					if (axios.isAxiosError(error) && error.response?.status === 409) {
						toast.error(
							'Cannot shorten trip dates. You have events scheduled on days that would be removed. Please delete or move those events first.',
							{ duration: 10000 }
						)
					} else {
						toast.error('Failed to update trip dates. Please try again.')
					}
				},
			})
		}
	}

	const handleIdeasClick = () => {
		navigate(`/trips/${tripId}/ideas`)
	}

	const handleMembersClick = () => {
		navigate(`/trips/${tripId}/members`)
	}

	const handleTripSettingsClick = () => {
		setIsEditTripModalOpen(true)
	}

	return (
		<DashboardWrapper>
			<DashboardContainer>
				<DashboardHeader>
					<DashboardTitleSection>
						<DashboardTitle>{trip.name}</DashboardTitle>
						<TripCodeContainer>
							<TripCodeLabel>Trip Code:</TripCodeLabel>
							<TripCode>{trip.code}</TripCode>
						</TripCodeContainer>
					</DashboardTitleSection>
					<SettingsButtonContainer>
						<SettingsButton onClick={handleTripSettingsClick}>
							<SettingsIcon width={20} height={20} fill="currentColor" />
							<span>Trip Settings</span>
						</SettingsButton>
					</SettingsButtonContainer>
				</DashboardHeader>

				<DashboardGrid>
				{/* Itinerary Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<CalendarIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Itinerary</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						{!hasTripDates ? (
							<EmptyStateMessage>
								Set trip dates to start planning your itinerary
							</EmptyStateMessage>
						) : (
							<>
								<DashboardCardMetrics>
									<DashboardCardMetric>
										<span className="metric-value">{eventsCount}</span>
										<span className="metric-label">Events</span>
									</DashboardCardMetric>
									<DashboardCardMetric>
										<span className="metric-value">{daysCount}</span>
										<span className="metric-label">Days</span>
									</DashboardCardMetric>
								</DashboardCardMetrics>
								<DateInfoContainer>
									<div>Start: {startDate}</div>
									<div>End: {endDate}</div>
								</DateInfoContainer>
							</>
						)}
					</DashboardCardContent>
					<DashboardCardFooter>
						<NavigateButton onClick={handleItineraryClick}>
							{!hasTripDates ? 'Set Trip Dates' : 'View Itinerary →'}
						</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Ideas Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<IdeaIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Ideas</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{ideasCount}</span>
								<span className="metric-label">Total</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
                    <DashboardCardDescription>
						Share and save potential trip ideas here. Such as social media links, locations and more that can later be added to your itinerary.
					</DashboardCardDescription>
					<DashboardCardFooter>
						<NavigateButton onClick={handleIdeasClick}>View Ideas →</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Members Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<GroupsIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Members</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<DashboardCardMetrics>
							<DashboardCardMetric>
								<span className="metric-value">{membersCount}</span>
								<span className="metric-label">Total</span>
							</DashboardCardMetric>
						</DashboardCardMetrics>
					</DashboardCardContent>
					<DashboardCardFooter>
						<NavigateButton onClick={handleMembersClick}>View Members →</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>

				{/* Expenses Card */}
				<DashboardCard>
					<DashboardCardHeader>
						<DashboardCardIcon>
							<ExpenseIcon width={24} height={24} fill="currentColor" />
						</DashboardCardIcon>
						<DashboardCardTitle>Expenses</DashboardCardTitle>
					</DashboardCardHeader>
					<DashboardCardContent>
						<ExpensesMessage>
							<div>You can only view trip expenses inside your iOS/Android app</div>
						</ExpensesMessage>
					</DashboardCardContent>
					<DashboardCardFooter>
						<NavigateButton disabled>
							View Expenses →
						</NavigateButton>
					</DashboardCardFooter>
				</DashboardCard>
				</DashboardGrid>
			</DashboardContainer>

		<EditTripDatesModal
			isOpen={isEditModalOpen}
			onClose={() => setIsEditModalOpen(false)}
			onSave={handleSaveTripDates}
			initialStartDate={tripStartDate}
			initialEndDate={tripEndDate}
			isUpdating={updateTripDates.isPending}
		/>

		<EditTripModal
			isOpen={isEditTripModalOpen}
			onClose={() => setIsEditTripModalOpen(false)}
			tripId={tripId || ''}
			initialName={trip.name}
			initialCurrencyCode={trip.defaultCurrencyCode}
			isOwner={me ? me.id === trip.ownerUserId : undefined}
		/>
	</DashboardWrapper>
)
}

export default TripDetailPage

